/**
 * What went on the ground.
 *
 * The one screen in this product where the record itself is the
 * regulated artefact. Everything else here records what the club did so
 * the club can run itself; this records what the club did because FIFRA
 * says it must, and an inspector may ask to see it.
 *
 * WHAT IS SHUT COMES FIRST, and it is first for an operational reason
 * rather than a visual one. A superintendent opens this at seven in the
 * morning to decide where to send a crew, and the one thing that can
 * hurt somebody is sending them onto ground still inside its re-entry
 * interval. CHEM-5 refuses the task, but a refusal at the moment of
 * assignment is a refusal delivered after the crew has been briefed.
 *
 * ONE TANK, ONE FORM. A superintendent does not apply one product to one
 * green — they mix three things and spray nine. A form with one product
 * and one area would make that morning eighteen rows that all have to
 * agree about the date, the wind, the applicator and the certificate,
 * and they stop agreeing the first time somebody edits one. So the form
 * takes the tank, the crew, the weather and every green together, and
 * posts them in one request — which is also what the schema requires:
 * CHEM-1 and CHEM-2 are deferred and ask their questions at COMMIT.
 *
 * THE APPLICATOR LIST CARRIES ITS EXPIRY. Offering a list of names
 * without saying whose certificate ran out in March would be a screen
 * that helps somebody break CHEM-1 — and the refusal would arrive after
 * the spray, at the moment somebody tries to record it.
 *
 * NO EDIT AND NO DELETE. CHEM-3: an applied record is history. A mistake
 * is cancelled with a reason and the cancelled row stays, because
 * history that can be rewritten is not a record anybody can rely on.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';
import { clubToday, dayLabel } from '../lib/sheet.ts';
import { date as fmtDate, dateTime } from '../lib/format.ts';

/* Shapes — mirroring the API's JSON one for one. */

interface Product {
  id: string;
  code: string;
  name: string;
  kind: string;
  epa_registration_number: string | null;
  is_restricted_use: boolean;
  signal_word: string | null;
  reentry_hours: string | null;
  rate_unit: string;
  nitrogen_percent: string | null;
}

interface License {
  id: string;
  person_id: string;
  person: string;
  license_number: string;
  category: string | null;
  expires_on: string;
  has_lapsed: boolean;
}

interface Hole {
  id: string;
  nine_id: string;
  hole_number: number;
  name: string | null;
  nine: string;
}

interface Setup {
  products: Product[];
  licenses: License[];
  courses: { id: string; name: string }[];
  holes: Hole[];
  elements: string[];
}

interface Application {
  id: string;
  applied_on: string;
  started_at: string | null;
  finished_at: string | null;
  status: string;
  target: string | null;
  wind_speed_kph: string | null;
  wind_direction: string | null;
  applicator: string;
  license_number: string | null;
  area_sq_m: string;
  areas: string;
  products: string | null;
  restricted: boolean | null;
  reentry_until: string | null;
}

interface Restricted {
  hole_id: string | null;
  hole_number: number | null;
  element: string;
  application_id: string;
  applied_on: string;
  products: string;
  reentry_until: string;
  applicator: string;
}

interface Nutrient {
  element: string;
  area_sq_m: string;
  product_applied: string;
  nitrogen_applied: string;
  phosphate_applied: string;
  potash_applied: string;
}

const ELEMENT_SAYS: Record<string, string> = {
  green: 'Greens',
  tee: 'Tees',
  fairway: 'Fairways',
  bunker: 'Bunkers',
  rough: 'Rough',
  cart_path: 'Cart paths',
  practice: 'Practice ground',
  general: 'General',
};

/** One kilogram of nothing rounded away: these are compliance figures. */
function n2(v: string | null): string {
  if (v === null) return '—';
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function AgronomyPage() {
  const { club, runs } = useSession();
  const mayRecord = runs('grounds');

  const setup = useApi<Setup>('/v1/agronomy/setup');
  const restricted = useApi<{ restricted: Restricted[] }>('/v1/agronomy/restricted');
  const log = useApi<{ applications: Application[] }>('/v1/agronomy/applications');
  const nutrients = useApi<{ nutrients: Nutrient[] }>('/v1/agronomy/nutrients');

  const [recording, setRecording] = useState(false);

  function refreshAll() {
    restricted.reload();
    log.reload();
    nutrients.reload();
  }

  return (
    <>
      <h1>What Went on the Ground</h1>

      {/* ============================================================= */}
      {/* Shut to work. First, because it is the one thing here that can
          hurt somebody. */}
      <Card title="Shut to Work">
        <ErrorBanner error={restricted.error} />
        {restricted.loading ? (
          <Loading rows={2} />
        ) : (restricted.data?.restricted ?? []).length === 0 ? (
          <Empty>Nothing is inside a re-entry interval. The whole course can be worked.</Empty>
        ) : (
          <table className="data data--cards">
            <thead>
              <tr>
                <th style={{ width: 160 }}>Where</th>
                <th>What went on it</th>
                <th style={{ width: 200 }}>Nobody works it until</th>
              </tr>
            </thead>
            <tbody>
              {(restricted.data?.restricted ?? []).map((r) => (
                <tr key={`${r.application_id}-${r.hole_id ?? r.element}`}>
                  <td>
                    {r.hole_number !== null
                      ? `Hole ${r.hole_number} · ${ELEMENT_SAYS[r.element] ?? r.element}`
                      : ELEMENT_SAYS[r.element] ?? r.element}
                  </td>
                  <td>
                    {r.products}
                    <div className="muted small">
                      {fmtDate(r.applied_on)} · {r.applicator}
                    </div>
                  </td>
                  <td>
                    <Status tone="serious">{dateTime(r.reentry_until)}</Status>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ============================================================= */}
      {mayRecord && (
        recording ? (
          <RecordTank
            setup={setup.data}
            today={clubToday(club?.timezone)}
            onDone={() => {
              setRecording(false);
              refreshAll();
            }}
            onCancel={() => setRecording(false)}
          />
        ) : (
          <Card title="Record a Tank">
            <div className="row" style={{ gap: 12, alignItems: 'center' }}>
              <button onClick={() => setRecording(true)}>Record what went out</button>
              <span className="muted small">
                One tank, one crew, one morning — with every area it covered.
              </span>
            </div>
          </Card>
        )
      )}

      {/* ============================================================= */}
      <Card title="The Log" flush>
        <ErrorBanner error={log.error} />
        {log.loading ? (
          <Loading rows={4} />
        ) : (log.data?.applications ?? []).length === 0 ? (
          <Empty>Nothing recorded yet.</Empty>
        ) : (
          <table className="data data--cards">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Day</th>
                <th>What, and on what</th>
                <th style={{ width: 180 }}>Applicator</th>
                <th style={{ width: 130 }}>Area</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {(log.data?.applications ?? []).map((a) => (
                <tr key={a.id} className={a.status === 'cancelled' ? 'muted' : undefined}>
                  <td className="mono">
                    {fmtDate(a.applied_on)}
                    {a.started_at && (
                      <div className="muted small">
                        {a.started_at.slice(0, 5)}
                        {a.finished_at ? `–${a.finished_at.slice(0, 5)}` : ''}
                      </div>
                    )}
                  </td>
                  <td>
                    {a.products ?? <span className="muted">nothing recorded</span>}
                    <div className="muted small">
                      {a.target ? `${a.target} · ` : ''}
                      {a.areas} {Number(a.areas) === 1 ? 'area' : 'areas'}
                      {a.wind_speed_kph
                        ? ` · wind ${n2(a.wind_speed_kph)} kph ${a.wind_direction ?? ''}`
                        : ''}
                    </div>
                  </td>
                  <td>
                    {a.applicator}
                    {/* The certificate the record CLAIMS covered it. A
                        blank here on a restricted-use spray is what an
                        inspector looks for, so it is shown rather than
                        left to the detail page. */}
                    <div className="muted small mono">{a.license_number ?? '—'}</div>
                  </td>
                  <td className="mono">{n2(a.area_sq_m)} m²</td>
                  <td>
                    {a.status === 'cancelled'
                      ? <Status tone="neutral">Cancelled</Status>
                      : a.restricted
                        ? <Status tone="warning">Restricted use</Status>
                        : <Status tone="good">Recorded</Status>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ============================================================= */}
      <Card title="This Year’s Nutrients" flush>
        <ErrorBanner error={nutrients.error} />
        <p className="muted small" style={{ padding: '12px 16px 0', margin: 0 }}>
          Fertilizer only, because a pesticide has no analysis to count. This is what a
          club on a watershed files, and the reason a fertilizer cannot be entered
          without its numbers.
        </p>
        {(nutrients.data?.nutrients ?? []).length === 0 ? (
          <Empty>Nothing has gone down this year.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Ground</th>
                <th style={{ width: 130 }}>Area</th>
                <th style={{ width: 110 }}>N</th>
                <th style={{ width: 110 }}>P₂O₅</th>
                <th style={{ width: 110 }}>K₂O</th>
              </tr>
            </thead>
            <tbody>
              {(nutrients.data?.nutrients ?? []).map((n) => (
                <tr key={n.element}>
                  <td>{ELEMENT_SAYS[n.element] ?? n.element}</td>
                  <td className="mono">{n2(n.area_sq_m)} m²</td>
                  <td className="mono">{n2(n.nitrogen_applied)}</td>
                  <td className="mono">{n2(n.phosphate_applied)}</td>
                  <td className="mono">{n2(n.potash_applied)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/**
 * The tank.
 *
 * Products and areas are both lists on one form, and both post with the
 * application in a single request. Splitting them into three saves would
 * not merely be more clicks — it would be refused, because CHEM-1 and
 * CHEM-2 are deferred constraint triggers that ask their questions about
 * the end of the transaction.
 */
function RecordTank({
  setup,
  today,
  onDone,
  onCancel,
}: {
  setup: Setup | null;
  today: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [appliedOn, setAppliedOn] = useState(today);
  const [applicator, setApplicator] = useState('');
  const [startedAt, setStartedAt] = useState('06:00');
  const [finishedAt, setFinishedAt] = useState('08:00');
  const [target, setTarget] = useState('');
  const [wind, setWind] = useState('');
  const [windDir, setWindDir] = useState('');
  const [temp, setTemp] = useState('');

  const [tank, setTank] = useState<{ product_id: string; rate: string }[]>([
    { product_id: '', rate: '' },
  ]);
  const [areas, setAreas] = useState<
    { hole_id: string; element: string; area: string }[]
  >([{ hole_id: '', element: 'green', area: '' }]);

  const save = useMutation(async () =>
    api('/v1/agronomy/applications', {
      method: 'POST',
      body: {
        applied_on: appliedOn,
        applicator_person_id: applicator,
        started_at: startedAt || null,
        finished_at: finishedAt || null,
        target: target.trim() || null,
        wind_speed_kph: wind === '' ? null : Number(wind),
        wind_direction: windDir || null,
        air_temp_c: temp === '' ? null : Number(temp),
        products: tank
          .filter((t) => t.product_id && t.rate)
          .map((t) => ({ product_id: t.product_id, rate_value: Number(t.rate) })),
        areas: areas
          .filter((a) => a.area)
          .map((a) => ({
            hole_id: a.hole_id || null,
            element: a.element,
            area_sq_m: Number(a.area),
          })),
      },
    }));

  const products = setup?.products ?? [];
  const licenses = setup?.licenses ?? [];
  const holes = setup?.holes ?? [];

  const chosen = products.find((p) => p.id === tank[0]?.product_id);
  const anyRestricted = tank.some(
    (t) => products.find((p) => p.id === t.product_id)?.is_restricted_use);

  // The certificate the chosen applicator holds ON THE DAY BEING
  // RECORDED, worked out here rather than after the save. CHEM-1 will
  // refuse it either way; the difference is whether somebody finds out
  // before or after they have typed nine areas in.
  const cover = licenses.find(
    (l) => l.person_id === applicator && l.expires_on >= appliedOn);

  return (
    <Card title="Record a Tank">
      <ErrorBanner error={save.error} />

      <div className="row row--wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
        <label>
          <span className="muted small">Day</span>
          <input type="date" value={appliedOn} onChange={(e) => setAppliedOn(e.target.value)} />
        </label>
        <label>
          <span className="muted small">From</span>
          <input type="time" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} />
        </label>
        <label>
          <span className="muted small">Finished</span>
          <input type="time" value={finishedAt} onChange={(e) => setFinishedAt(e.target.value)} />
        </label>
        <label>
          <span className="muted small">Applicator</span>
          <select value={applicator} onChange={(e) => setApplicator(e.target.value)}>
            <option value="">Choose…</option>
            {/* The expiry is in the label, not in a tooltip. */}
            {licenses.map((l) => (
              <option key={l.id} value={l.person_id}>
                {l.person} — {l.license_number}, to {l.expires_on}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="muted small">Target</span>
          <input
            value={target}
            placeholder="Dollar spot"
            onChange={(e) => setTarget(e.target.value)}
          />
        </label>
      </div>

      {/* The wind is a column rather than a note because several states
          require it and every drift complaint turns on it. */}
      <div className="row row--wrap" style={{ gap: 12, alignItems: 'flex-end', marginTop: 12 }}>
        <label>
          <span className="muted small">Wind (kph)</span>
          <input type="number" min="0" step="0.5" value={wind}
            onChange={(e) => setWind(e.target.value)} style={{ width: 100 }} />
        </label>
        <label>
          <span className="muted small">From</span>
          <select value={windDir} onChange={(e) => setWindDir(e.target.value)}>
            <option value="">—</option>
            {['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'].map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="muted small">Air (°C)</span>
          <input type="number" step="0.5" value={temp}
            onChange={(e) => setTemp(e.target.value)} style={{ width: 100 }} />
        </label>
      </div>

      {applicator && anyRestricted && !cover && (
        <p style={{ marginTop: 12 }}>
          <Status tone="serious">
            That person held no current certificate on {fmtDate(appliedOn)}, and something
            in this tank is restricted-use. This will be refused.
          </Status>
        </p>
      )}

      {/* ------------------------------------------------------------- */}
      <h3 style={{ marginTop: 20 }}>In the tank</h3>
      {tank.map((t, i) => {
        const p = products.find((x) => x.id === t.product_id);
        return (
          <div key={i} className="row row--wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
            <label>
              <span className="muted small">Product</span>
              <select
                value={t.product_id}
                onChange={(e) => setTank((v) =>
                  v.map((x, j) => (j === i ? { ...x, product_id: e.target.value } : x)))}
              >
                <option value="">Choose…</option>
                {products.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}{x.is_restricted_use ? ' (restricted use)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="muted small">Rate {p ? `(${p.rate_unit})` : ''}</span>
              <input
                type="number" min="0" step="0.01" style={{ width: 120 }}
                value={t.rate}
                onChange={(e) => setTank((v) =>
                  v.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x)))}
              />
            </label>
            {p?.reentry_hours && (
              <span className="muted small">
                shuts the ground for {n2(p.reentry_hours)} h
              </span>
            )}
            {tank.length > 1 && (
              <button className="secondary small"
                onClick={() => setTank((v) => v.filter((_, j) => j !== i))}>
                Remove
              </button>
            )}
          </div>
        );
      })}
      <button className="secondary small" style={{ marginTop: 8 }}
        onClick={() => setTank((v) => [...v, { product_id: '', rate: '' }])}>
        Another product
      </button>

      {/* ------------------------------------------------------------- */}
      <h3 style={{ marginTop: 20 }}>Where it went</h3>
      {areas.map((a, i) => (
        <div key={i} className="row row--wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
          <label>
            <span className="muted small">Hole</span>
            <select
              value={a.hole_id}
              onChange={(e) => setAreas((v) =>
                v.map((x, j) => (j === i ? { ...x, hole_id: e.target.value } : x)))}
            >
              {/* No hole is a real answer, not a blank one: the practice
                  ground, a nursery green and the clubhouse surrounds
                  are all sprayed and none of them is a hole. */}
              <option value="">Not a hole</option>
              {holes.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.nine} {h.hole_number}{h.name ? ` — ${h.name}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="muted small">Ground</span>
            <select
              value={a.element}
              onChange={(e) => setAreas((v) =>
                v.map((x, j) => (j === i ? { ...x, element: e.target.value } : x)))}
            >
              {(setup?.elements ?? []).map((el) => (
                <option key={el} value={el}>{ELEMENT_SAYS[el] ?? el}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="muted small">Area (m²)</span>
            <input
              type="number" min="0" step="1" style={{ width: 120 }}
              value={a.area}
              onChange={(e) => setAreas((v) =>
                v.map((x, j) => (j === i ? { ...x, area: e.target.value } : x)))}
            />
          </label>
          {areas.length > 1 && (
            <button className="secondary small"
              onClick={() => setAreas((v) => v.filter((_, j) => j !== i))}>
              Remove
            </button>
          )}
        </div>
      ))}
      <button className="secondary small" style={{ marginTop: 8 }}
        onClick={() => setAreas((v) => [...v, { hole_id: '', element: 'green', area: '' }])}>
        Another area
      </button>

      {/* ------------------------------------------------------------- */}
      <div className="row" style={{ gap: 8, marginTop: 20 }}>
        <button
          disabled={save.busy || !applicator}
          onClick={async () => {
            if (await save.run()) onDone();
          }}
        >
          {save.busy ? 'Recording…' : 'Record it'}
        </button>
        <button className="secondary" onClick={onCancel}>Cancel</button>
        <span className="muted small" style={{ alignSelf: 'center' }}>
          {chosen ? `${dayLabel(appliedOn, undefined)}` : ''}
        </span>
      </div>
    </Card>
  );
}
